import * as React from 'react';
import {
    AnyTimelineEventsFilter,
    TimelineEventsFilterType,
    DateFilter,
    ReleaseStepFilter,
    AutomationFilter,
} from '../../types/timelineEventsFilters';
import { compact, flatMap } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import Form from 'antd/lib/form';
import { updateTimelineEventsFilters } from '../../actions/filters';
import { RootState } from '../../reducers/reducers';
import {
    selectDefaultReleaseStepsForApp,
    selectTimelineEventsFiltersMap,
    selectAppStoreType,
    selectIsWorkflowsCIStepEnabled,
} from '../../selectors/selectors';
import Space from 'antd/lib/space';
import { antGrey } from '../styles/globalStyles';
import Button from 'antd/lib/button';
import Select from 'antd/lib/select';
import { filterOptionValue } from '../../helpers/formHelpers';
import {
    displayTextForReleaseStepType,
    automationDisplayNameForAutomationId,
    iconForAutomationType,
} from '../../helpers/displayHelpers';
import { AppPlatform, AppStoreType, TimelineEventType, ReleaseStepType, AutomationType } from '../../types';
import DatePicker from 'antd/lib/date-picker';
import Icon from '@ant-design/icons';

type TimelineEventAutomationPair = [TimelineEventType, AutomationType];

interface TimelineEventsFiltersComponentProps {
    appId: string;
    isVisible: boolean;
    onApplyFilters: (filters: AnyTimelineEventsFilter[]) => void;
    platform: AppPlatform;
    appStoreType: AppStoreType;
    releaseStepType?: ReleaseStepType;
}

export const TimelineEventsFiltersComponent: React.FC<TimelineEventsFiltersComponentProps> = (
    props: TimelineEventsFiltersComponentProps,
) => {
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const { RangePicker } = DatePicker;

    const timelineAutomations: TimelineEventAutomationPair[] = [
        [TimelineEventType.AppStoreVersionAppStoreBuildChanged, AutomationType.AutoSelectLatestBuild],
        [TimelineEventType.AppStoreVersionCreated, AutomationType.AutoCreateAppStoreVersions],
        [TimelineEventType.AutoDownloadArtifacts, AutomationType.AutoDownloadArtifacts],
        [TimelineEventType.AppStoreVersionReleaseNotesUpdated, AutomationType.AutoApplyMetadata],
        [TimelineEventType.ReleaseCreated, AutomationType.AutoCreateRunwayReleases],
        [TimelineEventType.ReleaseCreatedUpcomingRelease, AutomationType.AutoCreateRunwayReleases],
        [TimelineEventType.ReleaseTagged, AutomationType.AutoTagReleases],
        [TimelineEventType.PRMerged, AutomationType.AutoMergeRunwayPRs],
        [TimelineEventType.FeatureAffiliationsApplied, AutomationType.AutoAddMissingFAs],
        [TimelineEventType.ReleaseBranchCreated, AutomationType.AutoKickoffRelease],
        [TimelineEventType.BuildWorkflowTriggered, AutomationType.AutoKickoffTriggerWorkflow],
        [TimelineEventType.IssueTrackingReleaseCompleted, AutomationType.AutoCompleteIssueTrackingReleases],
        [TimelineEventType.IssueTrackingTicketsTransitionedToStatus, AutomationType.AutoTransitionIssueTrackingTickets],
        [TimelineEventType.ReleaseSubmitted, AutomationType.AutoSubmitRelease],
        [TimelineEventType.ReleaseReleased, AutomationType.AutoRelease],
        [TimelineEventType.AppStoreVersionBetaSubmittedForReview, AutomationType.AutoSubmitBetaBuilds],
        [TimelineEventType.BetaBuildTestingGroupsApplied, AutomationType.AutoAssignBetaBuilds],
        [TimelineEventType.ReleaseVersionBumped, AutomationType.AutoBumpVersion],
        [TimelineEventType.BetaBuildPromotedToTestTrack, AutomationType.AutoPromoteBuildsToTestTrack],
        [TimelineEventType.AppStoreVersionPhasedReleaseUpdated, AutomationType.AutoPromoteStablePhasedReleases],
        [TimelineEventType.AppStoreVersionPhasedReleaseUpdated, AutomationType.AutoHaltUnstablePhasedReleases],
        [TimelineEventType.CommsChannelCreated, AutomationType.AutoCreateReleaseChannels],
        [TimelineEventType.DefaultReviewAttachmentUpdated, AutomationType.AutoApplyReviewAttachments],
        [TimelineEventType.BetaBuildTestingNotesAutoApplied, AutomationType.AutoApplyBetaBuildNotes],
        [TimelineEventType.ReleaseBranchBackmergePerformed, AutomationType.AutoBackmergeReleaseBranches],
        [TimelineEventType.AppStoreUsersSynced, AutomationType.AutoSyncAppStoreUsers],
        [TimelineEventType.ReleaseBranchDeleted, AutomationType.AutoDeleteReleaseBranch],
        [TimelineEventType.FixRequestApprovalStatusUpdated, AutomationType.AutoCherryPickFromWorkingBranch],
        [TimelineEventType.BinaryArtifactAppStoreUploadSuccess, AutomationType.AutoUploadBinaryArtifactToAppStore],
        [
            TimelineEventType.DsymArtifactStabilityMonitoringUploadSuccess,
            AutomationType.AutoUploadDsymsToStabilityMonitoring,
        ],
        [TimelineEventType.AppStoreVersionPhasedReleaseUpdated, AutomationType.AutoStagedRollout],
        [TimelineEventType.ProvisioningProfileDevicesSynced, AutomationType.AutoSyncAppleProvisioningProfilesDevices],
        [TimelineEventType.DevicesSynced, AutomationType.AutoSyncAppleDevices],
        [TimelineEventType.ReleaseCreated, AutomationType.AutoPrepareRollbackRelease],
        [TimelineEventType.BuildResignStarted, AutomationType.AutoResignRollbackBuild],
        [TimelineEventType.ReleaseSummarySent, AutomationType.AutoDistributeReleaseSummary],
        [TimelineEventType.CommsGroupsSynced, AutomationType.AutoSyncSlackGroups],
        [TimelineEventType.CommsGroupsSynced, AutomationType.AutoSyncTeamsTags],
        [TimelineEventType.TestRunCreated, AutomationType.AutoCreateTestRuns],
        [TimelineEventType.LocalizableStringsCherryPicked, AutomationType.AutoCherryPickLocalizableStrings],
        [TimelineEventType.TranslationsUploaded, AutomationType.AutoSyncTranslations],
        [TimelineEventType.MetadataTranslationsUploaded, AutomationType.AutoSyncMetadataTranslations],
        [TimelineEventType.BuildDetailsPostedToTicket, AutomationType.AutoPostBuildInfoOnPMTickets],
        [TimelineEventType.SyncedVCSMetadata, AutomationType.AutoSyncMetadataFromVCS],
    ];

    const currentFiltersMap = useSelector((state: RootState) => selectTimelineEventsFiltersMap(state));
    const releaseSteps = useSelector((state: RootState) => selectDefaultReleaseStepsForApp(state, props.appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, props.appId));
    const isWorkflowsCIStepEnabled = useSelector((state: RootState) =>
        selectIsWorkflowsCIStepEnabled(state, props.appId),
    );

    const applyFilters = (values: Record<string, any>) => {
        const filters: AnyTimelineEventsFilter[] = compact(
            flatMap(values, (_, type) => {
                switch (type) {
                    case TimelineEventsFilterType.Date: {
                        if (values.date) {
                            const startDate = values.date[0];
                            const endDate = values.date[1];

                            return {
                                type,
                                date: [startDate, endDate],
                            };
                        }
                        return null;
                    }
                    case TimelineEventsFilterType.ReleaseStep: {
                        if (values.releaseStep) {
                            return values.releaseStep.map((releaseStep) => {
                                return {
                                    type,
                                    releaseStep,
                                };
                            });
                        }
                        return null;
                    }
                    case TimelineEventsFilterType.Automation: {
                        if (values.automation) {
                            return values.automation.map((automation) => {
                                return {
                                    type,
                                    automation,
                                };
                            });
                        }
                        return null;
                    }
                }
                // unsupported filters
                return null;
            }),
        );

        dispatch(updateTimelineEventsFilters({ filters }));

        props.onApplyFilters(filters);
    };

    const clearButtonClicked = () => {
        dispatch(updateTimelineEventsFilters({ filters: [] }));
        form.resetFields();
    };

    React.useEffect(() => {
        if (Object.keys(currentFiltersMap).length === 0) {
            form.resetFields();
        }
    }, [currentFiltersMap]);

    const initialValues: Record<TimelineEventsFilterType, unknown> = {
        date:
            currentFiltersMap[TimelineEventsFilterType.Date] ?
                [
                    (currentFiltersMap[TimelineEventsFilterType.Date] as DateFilter[])[0].date[0],
                    (currentFiltersMap[TimelineEventsFilterType.Date] as DateFilter[])[0].date[1],
                ]
            :   null,
        releaseStep:
            currentFiltersMap[TimelineEventsFilterType.ReleaseStep] ?
                (currentFiltersMap[TimelineEventsFilterType.ReleaseStep] as ReleaseStepFilter[]).map(
                    (filter) => filter.releaseStep,
                )
            :   [],
        automation:
            currentFiltersMap[TimelineEventsFilterType.Automation] ?
                (currentFiltersMap[TimelineEventsFilterType.Automation] as AutomationFilter[]).map(
                    (filter) => filter.automation,
                )
            :   [],
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
            <div
                style={{
                    paddingLeft: 16,
                    paddingRight: 16,
                    paddingTop: 18,
                    paddingBottom: 18,
                    maxHeight: 450,
                    overflowY: 'scroll',
                }}
            >
                <Form
                    form={form}
                    initialValues={initialValues}
                    onFinish={applyFilters}
                    preserve={false}
                    layout="vertical"
                    className="filters-form"
                >
                    <Space direction="vertical" size={12}>
                        <Form.Item name="date" label="Event date">
                            <RangePicker />
                        </Form.Item>
                        {!props.releaseStepType && (
                            <Form.Item name="releaseStep" label="Release steps">
                                <Select
                                    allowClear
                                    showSearch
                                    mode="multiple"
                                    placeholder="Select release steps"
                                    filterOption={filterOptionValue}
                                    maxTagCount={3}
                                    style={{ maxWidth: 400 }}
                                >
                                    {releaseSteps.map((releaseStepType, index) => {
                                        return (
                                            <Select.Option key={index + 1} value={releaseStepType}>
                                                {displayTextForReleaseStepType(
                                                    releaseStepType,
                                                    props.platform,
                                                    props.appStoreType,
                                                    isWorkflowsCIStepEnabled,
                                                )}
                                            </Select.Option>
                                        );
                                    })}
                                </Select>
                            </Form.Item>
                        )}
                        <Form.Item name="automation" label="Automations">
                            <Select
                                allowClear
                                showSearch
                                mode="multiple"
                                placeholder="Select automations"
                                filterOption={filterOptionValue}
                                maxTagCount={3}
                                style={{ maxWidth: 400 }}
                            >
                                {timelineAutomations.map(([timelineAutomationType, automationType], index) => {
                                    const automationDisplayName = automationDisplayNameForAutomationId(
                                        automationType,
                                        appStoreType,
                                    );
                                    if (automationDisplayName) {
                                        return (
                                            <Select.Option key={index + 1} value={timelineAutomationType}>
                                                <Icon
                                                    component={iconForAutomationType(automationType)}
                                                    style={{
                                                        color: antGrey.grey7,
                                                        marginRight: 8,
                                                    }}
                                                />
                                                {automationDisplayName}
                                            </Select.Option>
                                        );
                                    } else {
                                        return <></>;
                                    }
                                })}
                            </Select>
                        </Form.Item>
                    </Space>
                </Form>
            </div>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: 12,
                    backgroundColor: antGrey.grey3,
                }}
            >
                <Button size="small" onClick={clearButtonClicked}>
                    Clear
                </Button>
                <Button size="small" onClick={form.submit} type="primary">
                    Apply
                </Button>
            </div>
        </div>
    );
};
